const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]'
const TABINDEX_STORE = 'a11yTabindex' // dataset key -> data-a11y-tabindex
const PENDING_HIDE_STORE = 'a11yPendingHide' // dataset key -> data-a11y-pending-hide
const FOCUSOUT_BOUND_STORE = 'a11yFocusoutBound' // dataset key -> data-a11y-focusout-bound

function focusablesIn(el) {
  return Array.prototype.slice.call(el.querySelectorAll(FOCUSABLE_SELECTOR))
}

function setSlideHidden(slide, hidden) {
  if (hidden && slide.contains(document.activeElement)) {
    slide.dataset[PENDING_HIDE_STORE] = 'true'
    if (!slide.dataset[FOCUSOUT_BOUND_STORE]) {
      slide.dataset[FOCUSOUT_BOUND_STORE] = 'true'
      slide.addEventListener('focusout', function onFocusOut(event) {
        // Ignore focus moves that stay within the slide.
        if (slide.contains(event.relatedTarget)) return
        slide.removeEventListener('focusout', onFocusOut)
        delete slide.dataset[FOCUSOUT_BOUND_STORE]
        if (slide.dataset[PENDING_HIDE_STORE] === 'true') {
          delete slide.dataset[PENDING_HIDE_STORE]
          setSlideHidden(slide, true)
        }
      })
    }
    return
  }

  // A newer request to show the slide cancels any deferred hide.
  if (!hidden) delete slide.dataset[PENDING_HIDE_STORE]

  if (hidden) {
    slide.setAttribute('aria-hidden', 'true')
    slide.setAttribute('inert', '')
    focusablesIn(slide).forEach((node) => {
      if (!(TABINDEX_STORE in node.dataset)) {
        node.dataset[TABINDEX_STORE] = node.getAttribute('tabindex') || ''
      }
      node.setAttribute('tabindex', '-1')
    })
  } else {
    slide.setAttribute('aria-hidden', 'false')
    slide.removeAttribute('inert')
    focusablesIn(slide).forEach((node) => {
      if (!(TABINDEX_STORE in node.dataset)) return
      const original = node.dataset[TABINDEX_STORE]
      if (original === '') node.removeAttribute('tabindex')
      else node.setAttribute('tabindex', original)
      delete node.dataset[TABINDEX_STORE]
    })
  }
}

class HHPodcastCarousel extends HTMLElement {
  constructor() {
    super()

    this.sectionId = this.getAttribute('data-section-id')

    this.carouselEl = this.querySelector('[data-podcast-carousel]')
    this.regionEl = this.querySelector('[data-podcast-region]')
    this.prevBtn = this.querySelector('.js-swiper-button-prev')
    this.nextBtn = this.querySelector('.js-swiper-button-next')
    this.instructions = this.querySelector('.js-podcast-carousel-instructions')

    this.mobileSeriesSelect = this.querySelector(
      `#${this.sectionId}-series-filter-mobile`
    )
    this.mobileSecondaryFilterSelect = this.querySelector(
      `#${this.sectionId}-secondary-filter-mobile`
    )
    this.seriesRadioInputs = this.querySelectorAll('[data-series-filter]')
    this.secondaryFiltersContainer = this.querySelector(
      '[data-secondary-filters]'
    )
    this.secondaryFilterRadioInputs = this.querySelectorAll(
      '[data-secondary-filter]'
    )
    this.defaultSecondaryFilterRadioInput = this.querySelector(
      '[data-secondary-filter][value=""]'
    )
    this.slides = this.querySelectorAll('[data-podcast-carousel-slide]')

    this.handleSeriesFilterChange = this.handleSeriesFilterChange.bind(this)
    this.handleSecondaryFilterChange =
      this.handleSecondaryFilterChange.bind(this)

    this.seriesFilter =
      this.querySelector('[data-series-filter]:checked')?.value ?? ''
    this.secondaryFilter = ''
  }

  connectedCallback() {
    this.init()

    this.mobileSeriesSelect?.addEventListener(
      'change',
      this.handleSeriesFilterChange
    )
    this.mobileSecondaryFilterSelect?.addEventListener(
      'change',
      this.handleSecondaryFilterChange
    )

    this.seriesRadioInputs.forEach((input) => {
      input.addEventListener('change', this.handleSeriesFilterChange)
    })
    this.secondaryFilterRadioInputs.forEach((input) => {
      input.addEventListener('change', this.handleSecondaryFilterChange)
    })
  }

  disconnectedCallback() {
    this.mobileSeriesSelect?.removeEventListener(
      'change',
      this.handleSeriesFilterChange
    )
    this.mobileSecondaryFilterSelect?.removeEventListener(
      'change',
      this.handleSecondaryFilterChange
    )

    this.seriesRadioInputs.forEach((input) => {
      input.removeEventListener('change', this.handleSeriesFilterChange)
    })
    this.secondaryFilterRadioInputs.forEach((input) => {
      input.removeEventListener('change', this.handleSecondaryFilterChange)
    })
  }

  handleSeriesFilterChange(event) {
    this.seriesFilter = event.target.value

    this.secondaryFilter = ''
    if (this.defaultSecondaryFilterRadioInput) {
      this.defaultSecondaryFilterRadioInput.checked = true
    }
    if (this.mobileSecondaryFilterSelect) {
      this.mobileSecondaryFilterSelect.value = ''
    }

    this.filterSlides(true)
  }

  handleSecondaryFilterChange(event) {
    this.secondaryFilter = event.target.value

    this.filterSlides()
  }

  filterSlides(reRenderSecondaryFilters) {
    const requiredSecondaryFilters = new Set()

    this.slides.forEach((slide) => {
      const series = slide.getAttribute('data-series')
      const secondaryFilterValue = slide.getAttribute(
        'data-secondary-filter-value'
      )

      if (
        series === this.seriesFilter &&
        (!this.secondaryFilter || secondaryFilterValue === this.secondaryFilter)
      ) {
        slide.classList.remove('hidden')

        if (secondaryFilterValue) {
          requiredSecondaryFilters.add(secondaryFilterValue)
        }
      } else {
        slide.classList.add('hidden')
      }
    })

    if (reRenderSecondaryFilters && this.secondaryFiltersContainer) {
      this.updateSecondaryFilters(requiredSecondaryFilters)
    }

    this.swiperCarousel.update()
  }

  updateSecondaryFilters(filtersSet) {
    if (filtersSet.size > 1) {
      this.secondaryFiltersContainer.classList.remove('hidden')

      this.secondaryFilterRadioInputs.forEach((input) => {
        if (filtersSet.has(input.value) || input.value === '') {
          input.parentElement.classList.remove('hidden')

          this.mobileSecondaryFilterSelect
            ?.querySelector(`option[value='${input.value}']`)
            ?.classList.remove('hidden')
        } else {
          input.parentElement.classList.add('hidden')

          this.mobileSecondaryFilterSelect
            ?.querySelector(`option[value='${input.value}']`)
            ?.classList.add('hidden')
        }
      })

      if (this.mobileSecondaryFilterSelect) {
        this.mobileSecondaryFilterSelect.disabled = false
      }
    } else {
      this.secondaryFiltersContainer.classList.add('hidden')
      if (this.mobileSecondaryFilterSelect) {
        this.mobileSecondaryFilterSelect.disabled = true
      }
    }
  }

  init() {
    if (!this.carouselEl) return

    const updateA11y = (swiper) => {
      const hasControls = this.hasOperableControls()
      this.updateSlideVisibility(swiper, hasControls)
      this.updateNavAvailability(hasControls)
    }

    this.swiperCarousel = new Swiper(this.carouselEl, {
      slidesPerView: 1.5,
      spaceBetween: 5,
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
        slideRole: 'group',
        itemRoleDescriptionMessage: 'slide',
        prevSlideMessage:
          this.prevBtn?.getAttribute('aria-label') || 'Previous slide',
        nextSlideMessage:
          this.nextBtn?.getAttribute('aria-label') || 'Next slide',
      },
      navigation: {
        nextEl: this.nextBtn,
        prevEl: this.prevBtn,
      },
      breakpoints: {
        750: {
          slidesPerView: 2.5,
        },
        990: {
          slidesPerView: 4.5,
        },
      },
      on: {
        init: updateA11y,
        slideChange: updateA11y,
        transitionEnd: updateA11y,
        resize: updateA11y,
        breakpoint: updateA11y,
        slidesLengthChange: updateA11y,
        update: updateA11y,
      },
    })
  }

  hasOperableControls() {
    const isOperable = (btn) => !!(btn && btn.offsetParent !== null)
    return isOperable(this.prevBtn) || isOperable(this.nextBtn)
  }

  updateSlideVisibility(swiper, hasControls = this.hasOperableControls()) {
    const instance = swiper || this.swiperCarousel
    if (!instance || !instance.slides) return
    Array.prototype.forEach.call(instance.slides, (slide) => {
      if (!hasControls) {
        setSlideHidden(slide, false)
        return
      }
      const isVisible = slide.classList.contains('swiper-slide-visible')
      setSlideHidden(slide, !isVisible)
    })
  }

  updateNavAvailability(hasControls = this.hasOperableControls()) {
    if (!this.instructions) return

    const describedEl = this.regionEl || this.carouselEl
    const navButtons = [this.prevBtn, this.nextBtn]

    if (hasControls) {
      this.instructions.hidden = false
      describedEl?.setAttribute('aria-describedby', this.instructions.id)
      navButtons.forEach((btn) => {
        btn?.setAttribute('aria-describedby', this.instructions.id)
      })
    } else {
      describedEl?.removeAttribute('aria-describedby')
      navButtons.forEach((btn) => btn?.removeAttribute('aria-describedby'))
      this.instructions.hidden = true
    }
  }
}

customElements.define('hh-podcast-carousel', HHPodcastCarousel)
